import { useEffect, useState } from 'react';
import { useSessionStore } from './store';
import { APP_CONFIG } from '../app/config';
import { identityApi } from '../api/client';

// SessionProvider mounts once at the app root.
// - In algosys-embedded mode: listens for a postMessage from the parent frame.
// - In standalone-token mode: bootstraps current user from /auth/me if token exists.
// - In dev-open mode: no-op (session already ready from config).
export function SessionProvider({ children }) {
  const { mode, token, bootstrapFromParent, setSession, clearSession, ready } = useSessionStore();
  const [bootstrapping, setBootstrapping] = useState(
    mode === 'standalone-token' && !!token && !ready
  );

  // standalone-token: load current user from /auth/me using stored token
  useEffect(() => {
    if (mode !== 'standalone-token') return;
    if (!token) {
      // No token stored — set ready=true so the login page can render
      useSessionStore.setState({ ready: true });
      return;
    }
    if (ready) return; // already bootstrapped

    setBootstrapping(true);
    identityApi.me()
      .then((profile) => {
        setSession({ user: profile, token, roles: profile.roles });
      })
      .catch((err) => {
        // 401: token expired or invalid — clear session, login page will handle redirect
        if (err?.status === 401) clearSession();
        // Other errors: still mark ready so the app doesn't hang
        useSessionStore.setState({ ready: true });
      })
      .finally(() => setBootstrapping(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run only once on mount

  // algosys-embedded: listen for parent frame postMessage
  useEffect(() => {
    if (mode !== 'algosys-embedded') return;

    try {
      window.parent.postMessage({ type: 'ALGOSYS_READY' }, '*');
    } catch { /* cross-origin parent, ignore */ }

    const handleMessage = (event) => {
      if (event.data?.type === 'ALGOSYS_SESSION') {
        bootstrapFromParent(event.data.payload);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [mode, bootstrapFromParent]);

  // Show spinner while bootstrapping from token or waiting for embedded session
  if (bootstrapping || (mode === 'algosys-embedded' && !ready)) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg-base">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-mono text-txt-muted">
            {mode === 'algosys-embedded' ? 'Awaiting Algosys session...' : 'Loading session...'}
          </p>
        </div>
      </div>
    );
  }

  return children;
}
